import { useRef, useState } from 'react';
import styled from 'styled-components';
import {
  APIFieldAttributes,
  APIOrganizationUser,
  APIReferenceAttributes,
  ArdoqId,
} from '@ardoq/api-types';
import { t, type I18nLocale } from '@ardoq/i18n';
import { ComponentRepresentation } from '@ardoq/basic-components';
import type { RepresentationData } from '@ardoq/data-model';
import { colors, fontFamily } from '@ardoq/design-tokens';
import {
  COMPONENT_ID_ATTRIBUTE,
  REFERENCE_ID_ATTRIBUTE,
} from '@ardoq/global-consts';
import { getComponentLabelParts } from '@ardoq/graph';
import { canvasLabelMeasurer } from '@ardoq/graph-labeling';
import { CUSTOM_SVG_ICON_SELECTOR } from '@ardoq/icons';
import { type Locale, localeCompare } from '@ardoq/locale';
import { BulkHoverPopover, WithUncontrolledPopover } from '@ardoq/popovers';
import { fontSizeDefinitions_DEPRECATED } from '../../atomicComponents/fonts/fontSizeDefinitionsWithDefaultFont';
import { AqCollapsible } from 'atomicComponents/AqCollapsible/AqCollapsible';
import {
  PAGE_VIEW_MEDIUM_TEMP_FONT_SIZE,
  PAGE_VIEW_MEDIUM_TEMP_LINE_HEIGHT,
} from 'app/designTokens/fonts';
import {
  Header,
  HeaderAndTableContainer,
  printableTextStyles,
  TableRow,
  TableRowContainer,
} from 'tabview/pagesView/commonUIElements';
import {
  DEFAULT_NUMBER_OF_LIST_ITEMS_TO_SHOW,
  horizontalSpacing,
} from 'tabview/pagesView/constants';
import { Description } from 'tabview/pagesView/Description';
import { ExpandButton } from 'tabview/pagesView/ExpandButton';
import { Fields } from 'tabview/pagesView/Fields';
import { ReferencePopover } from 'tabview/pagesView/ReferencePopover';
import {
  SelfTruncatingComponentLabel,
  resolveTruncatedComponentLabelPopover,
} from 'tabview/SelfTruncatingComponentLabel';
import { contrastEnsuredComponentFill } from 'utils/modelCssManager/getCssColors';
import type {
  ComponentPathData,
  ComponentTag,
  ReferenceLabel,
} from 'tabview/pagesView/types';
import type { AttachmentWithContext } from 'streams/documentArchive/types';

const EMPTY_SET = new Set<string>();

const RowLabel = styled.div`
  ${fontSizeDefinitions_DEPRECATED.MEDIUM}
  color: ${colors.grey950};
  display: flex;
  align-items: center;
  justify-content: flex-start;
  overflow: hidden;
  span {
    font-weight: normal;
  }

  .printable & {
    align-items: flex-start;
    overflow-wrap: anywhere;
    overflow: unset;
  }
`;

const ComponentRepresentationContainer = styled.div`
  display: flex;
  justify-content: center;
  font-size: 18px;
  min-width: 18px;
  max-height: 18px;

  > ${CUSTOM_SVG_ICON_SELECTOR} {
    margin: 0;
    width: 18px;
    max-height: 18px;
    max-width: 18px;
  }
`;

const ComponentLink = styled.a`
  width: 100%;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  span {
    ${fontSizeDefinitions_DEPRECATED.MEDIUM}
    color: ${colors.grey950};
  }
  ${fontSizeDefinitions_DEPRECATED.MEDIUM}
  color: ${colors.grey950};
  margin-left: ${horizontalSpacing.SMALL};
  ${printableTextStyles}
`;

const ReferenceTypeLabel = styled.div`
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  display: inline-block !important;
  padding-left: 5px;
  ${printableTextStyles}
`;

const PageContentLayout = styled.div`
  margin: 0 0 ${horizontalSpacing.MEDIUM} 0;
  background: ${colors.grey200};
`;

type ComponentLabelProps = {
  componentId: ArdoqId;
  componentPath: ComponentPathData | undefined;
  isSource: boolean;
  displayText: string;
  style: object;
  bypassRenderLimit: boolean;
  representationData: RepresentationData | null;
  i18nLocale: I18nLocale;
};
const ComponentLabel = ({
  componentId,
  componentPath,
  isSource,
  displayText,
  style,
  bypassRenderLimit,
  representationData,
  i18nLocale,
}: ComponentLabelProps) => {
  const componentRepresentationData = representationData ?? {
    isImage: false,
    value: null,
  };
  const link = (
    <ComponentLink
      data-global-handler-id={componentId}
      style={{ fontWeight: 'bold' }}
      {...{ [COMPONENT_ID_ATTRIBUTE]: componentId }}
      className="component"
    >
      <SelfTruncatingComponentLabel
        {...getComponentLabelParts(componentId)}
        measure={measureReferencePageComponentLinkText}
        bypassRenderLimit={bypassRenderLimit}
        showTruncationPopover={!componentPath}
      />
    </ComponentLink>
  );
  return (
    <RowLabel
      style={{
        ...style,
        paddingLeft: horizontalSpacing.SMALL,
        color: contrastEnsuredComponentFill(componentId),
      }}
    >
      <ComponentRepresentationContainer>
        <ComponentRepresentation
          isImage={componentRepresentationData.isImage}
          value={componentRepresentationData.value}
          icon={componentRepresentationData.icon}
        />
      </ComponentRepresentationContainer>
      {componentPath ? (
        <WithUncontrolledPopover
          content={() => (
            <ReferencePopover
              componentPath={componentPath}
              isSource={isSource}
              displayText={displayText}
              i18nLocale={i18nLocale}
            />
          )}
        >
          {link}
        </WithUncontrolledPopover>
      ) : (
        link
      )}
    </RowLabel>
  );
};

const REFERENCE_PAGE_LINK_FONT = `bold ${PAGE_VIEW_MEDIUM_TEMP_FONT_SIZE}/${PAGE_VIEW_MEDIUM_TEMP_LINE_HEIGHT} ${fontFamily.regular}`;
const measureReferencePageComponentLinkText = canvasLabelMeasurer(
  REFERENCE_PAGE_LINK_FONT,
  1000
);

type ReferenceRowProps = {
  sourceComponentId: ArdoqId;
  targetComponentId: ArdoqId;
  sourceComponentPath: ComponentPathData | undefined;
  targetComponentPath: ComponentPathData | undefined;
  referenceId: ArdoqId;
  globalReferenceTypeName: string;
  description: string;
  fields: APIFieldAttributes[];
  attributes: APIReferenceAttributes | null;
  displayText: string;
  tags: ComponentTag[];
  usersById: Record<ArdoqId, APIOrganizationUser>;
  attachmentsById: Record<ArdoqId, AttachmentWithContext>;
  sourceRepresentationData: RepresentationData | null;
  targetRepresentationData: RepresentationData | null;
  bypassRenderLimit: boolean;
  defaultFields: Pick<APIFieldAttributes, 'label' | 'name' | 'type' | '_id'>[];
  hideEmptyFields: boolean;
  expandDescription: boolean;
  i18nLocale: I18nLocale;
  locale: Locale;
};

const ReferenceRow = ({
  sourceComponentId,
  targetComponentId,
  sourceComponentPath,
  targetComponentPath,
  referenceId,
  globalReferenceTypeName,
  description,
  fields,
  attributes,
  displayText,
  tags,
  usersById,
  attachmentsById,
  sourceRepresentationData,
  targetRepresentationData,
  bypassRenderLimit,
  defaultFields,
  hideEmptyFields,
  expandDescription,
  i18nLocale,
  locale,
}: ReferenceRowProps) => {
  const [shouldShowExpandButton, setShowExpandButton] = useState(false);
  return (
    <AqCollapsible
      defaultValue={false}
      renderContent={() => (
        <PageContentLayout {...{ [REFERENCE_ID_ATTRIBUTE]: referenceId }}>
          <Description
            text={description}
            bypassRenderLimit={bypassRenderLimit}
            expandDescription={expandDescription}
          />
          <Fields
            fields={fields}
            attributes={attributes}
            referenceTypeName={globalReferenceTypeName}
            usersById={usersById}
            attachmentsById={attachmentsById}
            tags={tags}
            defaultFields={defaultFields}
            redactedFieldNames={EMPTY_SET}
            modelId={referenceId}
            showHeader={false}
            hideEmptyFields={hideEmptyFields}
            i18nLocale={i18nLocale}
            locale={locale}
          />
        </PageContentLayout>
      )}
      renderTarget={({
        expand,
        collapse,
        isExpanded,
        toggle,
      }: {
        expand: () => void;
        collapse: () => void;
        isExpanded: boolean;
        toggle: () => void;
      }) => (
        <TableRow
          onClick={toggle}
          $active={isExpanded}
          onMouseEnter={() => setShowExpandButton(true)}
          onMouseLeave={() => setShowExpandButton(false)}
          $showCursor={true}
          {...{ [REFERENCE_ID_ATTRIBUTE]: referenceId }}
        >
          <ComponentLabel
            style={{ width: '30%' }}
            componentId={sourceComponentId}
            componentPath={sourceComponentPath}
            isSource={true}
            displayText={displayText}
            bypassRenderLimit={bypassRenderLimit}
            representationData={sourceRepresentationData}
            i18nLocale={i18nLocale}
          />
          <RowLabel style={{ fontStyle: 'italic', width: '20%' }}>
            <ReferenceTypeLabel>
              {globalReferenceTypeName}
              &nbsp;
              {/* nonbreaking space is needed because the last character will be clipped */}
            </ReferenceTypeLabel>
            {shouldShowExpandButton && (
              <ExpandButton
                marginTop={false}
                isExpanded={isExpanded}
                expand={shouldExpand => (shouldExpand ? expand() : collapse())}
                showLessString=""
                showMoreString=""
              />
            )}
          </RowLabel>
          <ComponentLabel
            style={{
              width: '50%',
            }}
            componentId={targetComponentId}
            componentPath={targetComponentPath}
            isSource={false}
            displayText={displayText}
            bypassRenderLimit={bypassRenderLimit}
            representationData={targetRepresentationData}
            i18nLocale={i18nLocale}
          />
        </TableRow>
      )}
    />
  );
};

type ReferencePageProps = {
  defaultFields: Pick<APIFieldAttributes, 'label' | 'name' | 'type' | '_id'>[];
  references: {
    incoming: ArdoqId[];
    outgoing: ArdoqId[];
  };
  referenceLabelById: Map<ArdoqId, ReferenceLabel>;
  componentRepresentationById: Map<ArdoqId, RepresentationData | null>;
  componentPathById: Map<ArdoqId, ComponentPathData>;
  tagsByModelId: Map<ArdoqId, ComponentTag[]>;
  usersById: Record<ArdoqId, APIOrganizationUser>;
  attachmentsById: Record<ArdoqId, AttachmentWithContext>;
  bypassRenderLimit: boolean;
  hideEmptyFields: boolean;
  expandDescription: boolean;
  i18nLocale: I18nLocale;
  locale: Locale;
};

const ReferencePage = ({
  references,
  referenceLabelById,
  componentRepresentationById,
  componentPathById,
  tagsByModelId,
  usersById,
  attachmentsById,
  bypassRenderLimit,
  defaultFields,
  expandDescription,
  hideEmptyFields,
  i18nLocale,
  locale,
}: ReferencePageProps) => {
  const [isExpanded, setIsExpanded] = useState(bypassRenderLimit);
  const containerRef = useRef<HTMLDivElement>(null);
  const showAllReferences = isExpanded || bypassRenderLimit;

  const allReferences = [...references.incoming, ...references.outgoing].sort(
    (a, b) => {
      const refTypeA = referenceLabelById.get(a)?.globalReferenceTypeName ?? '';
      const refTypeB = referenceLabelById.get(b)?.globalReferenceTypeName ?? '';
      return localeCompare(refTypeA, refTypeB, locale);
    }
  );
  const referencesToShow = showAllReferences
    ? allReferences
    : allReferences.slice(0, DEFAULT_NUMBER_OF_LIST_ITEMS_TO_SHOW);
  return referencesToShow.length > 0 ? (
    <>
      <BulkHoverPopover
        rootRef={containerRef}
        resolveContent={resolveTruncatedComponentLabelPopover}
      />
      <HeaderAndTableContainer ref={containerRef}>
        <Header>
          {t(
            i18nLocale,
            'tabview:pagesView.referencePage.referencesHeader',
            {
              incoming: String(references.incoming.length),
              outgoing: String(references.outgoing.length),
            },
            'References ({{incoming}} incoming, {{outgoing}} outgoing)'
          )}
        </Header>
        {referencesToShow.map((refId, i) => {
          const referenceLabel = referenceLabelById.get(refId);
          const sourceId = referenceLabel?.source ?? '';
          const targetId = referenceLabel?.target ?? '';
          return (
            <TableRowContainer key={`${refId}-${i}`}>
              <ReferenceRow
                sourceComponentId={sourceId}
                targetComponentId={targetId}
                sourceComponentPath={componentPathById.get(sourceId)}
                targetComponentPath={componentPathById.get(targetId)}
                referenceId={refId}
                globalReferenceTypeName={
                  referenceLabel?.globalReferenceTypeName ?? ''
                }
                description={referenceLabel?.description ?? ''}
                fields={referenceLabel?.fields ?? []}
                attributes={referenceLabel?.attributes ?? null}
                displayText={referenceLabel?.displayText ?? ''}
                tags={tagsByModelId.get(refId) ?? []}
                usersById={usersById}
                attachmentsById={attachmentsById}
                sourceRepresentationData={
                  componentRepresentationById.get(sourceId) ?? null
                }
                targetRepresentationData={
                  componentRepresentationById.get(targetId) ?? null
                }
                bypassRenderLimit={bypassRenderLimit}
                defaultFields={defaultFields}
                expandDescription={expandDescription}
                hideEmptyFields={hideEmptyFields}
                i18nLocale={i18nLocale}
                locale={locale}
              />
            </TableRowContainer>
          );
        })}
      </HeaderAndTableContainer>
      {allReferences.length > DEFAULT_NUMBER_OF_LIST_ITEMS_TO_SHOW &&
        !bypassRenderLimit && (
          <ExpandButton
            isExpanded={showAllReferences}
            expand={setIsExpanded}
            showLessString={t(
              i18nLocale,
              'tabview:pagesView.referencePage.showFewerReferences',
              'Show fewer references'
            )}
            showMoreString={t(
              i18nLocale,
              'tabview:pagesView.referencePage.showAllReferences',
              'Show all references'
            )}
          />
        )}
    </>
  ) : null;
};

export { ReferencePage };
